// Agent-template data layer (#288) — the UI half of a capability that had REST
// (`/v1/templates`) and MCP (`template`) doors but no human one.
//
// A template is the per-account "developer type" a box is pinned to: which
// program runs, how it is invoked, which backend/model/system prompt/KB scope it
// carries. `read:runners` reads, `write:runners` writes — the same gate as the
// fleet it configures.
//
// NO CURSOR, deliberately. `GET /v1/templates` answers the canonical
// `{data, next_cursor}` envelope with `next_cursor` ALWAYS null
// (`apps/api/src/routes/templates.ts`): templates are hand-authored operator
// config in the single digits, and the route serves the WHOLE collection because
// "a truncated list with nothing saying it was truncated is worse than a long
// one". A null cursor there is a true statement, not a placeholder — so this is a
// plain query, not `usePagedQuery`, and wiring a Load-more to it would advertise
// paging the route does not serve.
//
// A PUT is a FULL REPLACE on both transports: an omitted field resets to its
// column default rather than keeping its previous value. That is why the edit
// form loads the whole row and sends every field back — never a partial patch.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

/**
 * One template as `/v1/templates` serves it — the API's `TemplateView`
 * (`packages/mcp/src/army/template.ts`), snake_case, ISO timestamps. Restated
 * (not imported) because `@developerz/mcp` is not a dashboard dependency; the
 * field names are the wire contract both ends already publish.
 */
export type ApiTemplate = {
  readonly id: string;
  readonly name: string;
  readonly program: string;
  readonly invocation: unknown;
  readonly backend: string;
  readonly base_url: string | null;
  readonly model: string | null;
  readonly system_prompt: string | null;
  readonly kb_ref: string | null;
  readonly created_iso: string;
  readonly updated_iso: string;
};

/**
 * The mutable half of a template — every field a write sets, minus its name.
 * EVERY field is required here precisely because the API replaces every column:
 * a field this type let you omit would be a field a UI edit silently resets.
 */
export type TemplateWrite = {
  readonly program: string;
  readonly invocation: unknown;
  readonly backend: string;
  readonly base_url: string | null;
  readonly model: string | null;
  readonly system_prompt: string | null;
  readonly kb_ref: string | null;
};

export const TEMPLATES_QUERY_KEY = 'agent_templates';

/** The account's templates, name-ordered as the API serves them. Idle with no session. */
export function useTemplates(token: string | null = SESSION_TOKEN) {
  return createQuery<readonly ApiTemplate[]>(() => ({
    queryKey: [TEMPLATES_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () => (await apiFetch<Page<ApiTemplate>>('/v1/templates', token)).data,
  }));
}

/**
 * Create or replace `name`. The name is the PATH on purpose — one address, so
 * there is no body/path mismatch for the API to refuse — and a rename is a
 * create plus a delete, not a field.
 */
export async function saveTemplate(
  name: string,
  body: TemplateWrite,
  token: string | null = SESSION_TOKEN,
): Promise<ApiTemplate> {
  return (
    await apiFetch<{ template: ApiTemplate }>(`/v1/templates/${encodeURIComponent(name)}`, token, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  ).template;
}

/** Remove a template. Idempotent server-side (204 whether or not it existed). */
export async function deleteTemplate(
  name: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch<void>(`/v1/templates/${encodeURIComponent(name)}`, token, { method: 'DELETE' });
}
